import {useIntl, FormattedMessage} from 'react-intl';
import {useSelector} from 'react-redux';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router';
import {
  dataPrepareActions,
  DataPrepareStep,
  datasetLabelingApply,
  loadAnnotations,
  submitAnnotations,
  originToContainerMap,
  resumeDataPrep,
  revertDatasetMerge,
  mergeAnnotations,
} from '../../store/dataPrepare/dataPrepare';
import {DataView} from './DataUploadStep/DataView';
import {ClassificationView} from './ClassificationStep/ClassificationView';
import {Fade} from '@material-ui/core';
import {LabelView} from './LabelUploadStep/LabelView';
import Waiting from '../../base-components/StudioWaiting/Waiting';
import {MethodView} from './MethodSelectStep/MethodView';

import {
  AnnotationDataType,
  AnnotationType,
  DataPrepareType,
  DataPrepareTypeStr,
} from '../../types/dataset/DatasetGetAnnotationsResponse';
import {RootState, useAppDispatch} from '../../store';
import {projectFetch} from '../../store/project';
import {unwrapResult} from '@reduxjs/toolkit';
import {DetectionView} from './DetectionStep/DetectionView';
import {SegmentationView} from './SegmentationStep/SegmentationView';
import {DeploymentDataView} from './DeploymentDataStep/DeploymentDataView';
import {toast} from '../../base-components/StudioToast';
import {DataPreparationOrigin} from '../../types/dataset/DataPreparationContainer';
import {LabelMethodView} from './LabelMethodStep/LabelMethodView';
import {DataSetJobsProvider} from './contexts/DataSetJobsContext';
import {CreateDatasetStep} from './CreateDatasetStep/CreateDatasetStep';
import {datasetActions} from '../../store/dataService';
import {DataUploadingFinalization} from './DataUploadingFinalization/DataUploadingFinalization';

import './DataPrepare.scss';

const {reset, setStep, setName, setType, setStatus, setDescription} = dataPrepareActions;

type DataPrepareBaseProps = {
  isCreating?: boolean;
};

const DataPrepareWithVideoSupportBase = ({isCreating}: DataPrepareBaseProps) => {
  const intl = useIntl();
  const history = useHistory();
  const {projectId, context = 'raw'} = useParams<{
    projectId: string;
    context: DataPreparationOrigin;
  }>();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location]);
  const dispatch = useAppDispatch();

  const step = useSelector((state: RootState) => state.dataPrepare.step);
  const status = useSelector((state: RootState) => state.dataPrepare.status);
  const iatBusy = useSelector((state: RootState) => state.iat.isBusy);

  const type = useSelector((state: RootState) => state.dataPrepare.type);

  const uploadProgress = useSelector(
    (state: RootState) => state.dataPrepare.uploadProgress
  );
  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );

  const containers = useSelector((state: RootState) => state.dataPrepare.containers);
  const containerKey = useMemo(() => {
    const initialResult = originToContainerMap[context];

    let index = 0;
    let smartResult = initialResult;
    const options = ['dataset', 'deployment', 'raw']; // order of priority

    while (!containers[smartResult] && index < options.length) {
      smartResult = originToContainerMap[options[index] as DataPreparationOrigin];
      index++;
    }

    return !containers[smartResult] ? initialResult : smartResult;
  }, [context, containers]);
  const dataPreparationContainer = useMemo(() => containers[containerKey], [
    containers,
    containerKey,
  ]);
  const previousOrigin = dataPreparationContainer?.previous?.origin;
  const dataPreparationContainerId = useMemo(() => dataPreparationContainer?.id, [
    dataPreparationContainer,
  ]);

  const sourceDeploymentId = dataPreparationContainer?.sourceDeploymentId;

  const isLoading = status === 'LOADING';
  const isSubmitting = status === 'SUBMITTING';
  const isUploading = status === 'UPLOADING';
  const inProgress = isLoading || isSubmitting || isUploading || iatBusy;
  const imagesExistAlready = Boolean(annotationData?.images?.length);
  const labelsExistAlready = Boolean(annotationData?.categories?.length);

  const resume = useMemo(() => params.get('resume') === 'true', [params]);

  const storedProjectId = useSelector((state: RootState) => state.project.data?.id);
  const shouldUpdateProjectMemoryMetadata = useMemo(() => storedProjectId !== projectId, [
    storedProjectId,
    projectId,
  ]);

  const [creatingInitialization, setCreatingInitialization] = useState(false);
  useEffect(
    function resetDataPrepMemoryWhenIsCreating() {
      if (isCreating && !creatingInitialization) {
        setCreatingInitialization(true);
        dispatch(reset());
        dispatch(datasetActions.reset());
      }
    },
    [isCreating, creatingInitialization, dispatch]
  );

  useEffect(() => {
    if (projectId && context && resume) {
      if (shouldUpdateProjectMemoryMetadata) {
        dispatch(projectFetch(projectId));
      }

      dispatch(resumeDataPrep({projectId, context}));
    }

    return () => {
      dispatch(reset());
      dispatch(datasetActions.reset());
    };
  }, [projectId, context, dispatch, resume, shouldUpdateProjectMemoryMetadata]);

  const [loadAnnotationsInitialized, setLoadAnnotationsInitialized] = useState(false);
  const projectHasDatasetAssigned = useSelector((state: RootState) =>
    Boolean(state.project?.data?.dataSet?.id)
  );

  useEffect(
    function getAPIInitialAnnotations() {
      if (!loadAnnotationsInitialized && projectId && projectHasDatasetAssigned) {
        setLoadAnnotationsInitialized(true);
        dispatch(
          loadAnnotations({projectId, origin: dataPreparationContainer?.origin || 'raw'})
        );
      }
    },
    [
      projectId,
      dataPreparationContainer,
      loadAnnotationsInitialized,
      projectHasDatasetAssigned,
      dispatch,
    ]
  );

  useEffect(() => {
    const origin = dataPreparationContainer?.origin;

    const initializeDataPrep = async () => {
      dispatch(setStatus('LOADING'));

      if (!origin) {
        dispatch(setStep(DataPrepareStep.SELECT_PREPARE_TYPE));
        dispatch(setStatus('IDLE'));
      } else if (
        projectId &&
        (origin === 'raw' || origin === 'deployment' || origin === 'dataset')
      ) {
        try {
          const action = await dispatch(loadAnnotations({projectId, origin}));
          const initialAnnotationData = unwrapResult(action);
          const {categories, images, annotations, info} = initialAnnotationData;
          const name = dataPreparationContainer?.name;
          const purpose = dataPreparationContainer?.modelPurpose;
          const description = dataPreparationContainer?.description;

          if (purpose) {
            if (purpose === DataPrepareTypeStr.CLASSIFICATION) {
              dispatch(setType(DataPrepareType.CLASSIFICATION));
            } else if (purpose === DataPrepareTypeStr.DETECTION) {
              dispatch(setType(DataPrepareType.DETECTION));
            } else if (purpose === DataPrepareTypeStr.SEGMENTATION) {
              dispatch(setType(DataPrepareType.SEGMENTATION));
            }
          } else if (!type) {
            const isSegmentationType = Boolean(
              annotations?.find(annotation => annotation.segmentation?.polygon)
            );
            const isDetectionType =
              !isSegmentationType &&
              Boolean(annotations?.find(annotation => annotation.bbox));
            const type =
              origin === 'dataset'
                ? isSegmentationType
                  ? DataPrepareType.SEGMENTATION
                  : isDetectionType
                  ? DataPrepareType.DETECTION
                  : DataPrepareType.CLASSIFICATION
                : info?.type;

            console.warn('dataPreparationContainer has no purpose');

            if (type) {
              dispatch(setType(type));
            }
          }

          if (name) {
            dispatch(setName(name));
          }

          if (description) {
            dispatch(setDescription(description));
          }

          if (origin === 'deployment') {
            dispatch(setStep(DataPrepareStep.DEPLOYMENT_DATA_REVIEW));
          } else if (origin === 'raw') {
            if (type == null || name == null) {
              dispatch(setStep(DataPrepareStep.SELECT_PREPARE_TYPE));
            } else if (!images.length) {
              dispatch(setStep(DataPrepareStep.DATA_UPLOAD));
            } else if (!categories.length) {
              dispatch(setStep(DataPrepareStep.LABEL_UPLOAD));
            } else if (type === DataPrepareType.CLASSIFICATION) {
              dispatch(setStep(DataPrepareStep.CLASSIFICATION_PREPARE));
            } else if (type === DataPrepareType.DETECTION) {
              dispatch(setStep(DataPrepareStep.DETECTION_PREPARE));
            } else if (type === DataPrepareType.SEGMENTATION) {
              dispatch(setStep(DataPrepareStep.SEGMENTATION_PREPARE));
            }
          }
        } catch (e) {
          console.error(e);
          dispatch(setStep(DataPrepareStep.SELECT_PREPARE_TYPE));
        } finally {
          dispatch(setStatus('IDLE'));
        }
      }
    };

    initializeDataPrep();
  }, [projectId, dataPreparationContainer, type, dispatch]);

  const saveCurrentAnnotationsState = useCallback(
    async (finalAnnotations: AnnotationDataType) => {
      if (finalAnnotations && type !== null) {
        try {
          const origin = dataPreparationContainer?.origin;

          const action = await dispatch(
            origin === 'dataset'
              ? datasetLabelingApply({projectId, type})
              : submitAnnotations({projectId, annotationData: finalAnnotations, type})
          );

          // unwrapResult output is not used but it triggers the right outcome by throwing exception on a 500
          // if we dont have it the 500 result to the requests failing but goes to the design page.
          return unwrapResult<any>(action);
        } catch (error) {
          if (error instanceof Error) {
            toast.error({
              subtitle: error.message,
            });
          }
        } finally {
          dispatch(setStatus('IDLE'));
        }
      }
    },
    [dispatch, projectId, type, dataPreparationContainer]
  );

  const handleSubmit = useCallback(
    async (finalAnnotations: AnnotationDataType) => {
      if (type != null) {
        try {
          const res: any = await saveCurrentAnnotationsState(finalAnnotations);

          if (!(res?.data?.errors && res?.data?.errors.length)) {
            dispatch(reset());
            history.push(`/project/${projectId}/upload`);
          }
        } catch (error) {
          if (error instanceof Error) {
            toast.error({
              subtitle: error.message,
            });
          }
        } finally {
          dispatch(setStatus('IDLE'));
        }
      }
    },
    [history, projectId, type, dispatch, saveCurrentAnnotationsState]
  );

  const handleMergeRevert = async () => {
    try {
      await dispatch(revertDatasetMerge({projectId}));
      history.push(`/project/${projectId}/data/prepare/deployment`);
    } catch (e) {
      console.error(e);
    }
  };

  const [lastestDataMerged, setLastestDataMerged] = useState<string>('');
  const handleSaveAnnotations = useCallback(
    async (final: AnnotationType[], selectedImageId: number) => {
      if (dataPreparationContainerId && annotationData && final instanceof Array) {
        const validAnnotations = final.filter(
          annotation => annotation?.image_id === selectedImageId
        );
        const dataToMerge = {
          annotations: validAnnotations,
          images: [{id: selectedImageId}],
          resourceId: dataPreparationContainerId,
        };
        const dataToMergeString = JSON.stringify(dataToMerge);

        if (dataToMergeString !== lastestDataMerged) {
          setLastestDataMerged(dataToMergeString);
          // await dispatch(mergeAnnotations(dataToMerge));
        }
      }
    },
    [dataPreparationContainerId, annotationData, lastestDataMerged, dispatch]
  );

  const getNextStepForGoForward = useCallback(
    (currentStep: DataPrepareStep): DataPrepareStep | null => {
      switch (currentStep) {
        case DataPrepareStep.SELECT_PREPARE_TYPE:
          return DataPrepareStep.CREATING_DATASET;
        case DataPrepareStep.CREATING_DATASET:
          return imagesExistAlready
            ? DataPrepareStep.DATA_REVIEW
            : DataPrepareStep.DATA_UPLOAD;
        case DataPrepareStep.DATA_UPLOAD:
          return DataPrepareStep.DATA_REVIEW;
        case DataPrepareStep.DATA_REVIEW:
          return DataPrepareStep.DATA_REVIEW_FINALIZATION;
        case DataPrepareStep.DATA_REVIEW_FINALIZATION:
          return labelsExistAlready
            ? DataPrepareStep.LABEL_REVIEW
            : DataPrepareStep.LABEL_UPLOAD;
        case DataPrepareStep.LABEL_UPLOAD:
          return DataPrepareStep.LABEL_REVIEW;
        case DataPrepareStep.LABEL_REVIEW:
          return type === DataPrepareType.SEGMENTATION
            ? DataPrepareStep.SEGMENTATION_PREPARE
            : DataPrepareStep.SELECT_LABELLING_TYPE;
        case DataPrepareStep.SELECT_LABELLING_TYPE:
          return type === DataPrepareType.DETECTION
            ? DataPrepareStep.DETECTION_PREPARE
            : DataPrepareStep.CLASSIFICATION_PREPARE;
        default:
          return null;
      }
    },
    [imagesExistAlready, labelsExistAlready, type]
  );

  const goForward = useCallback(
    (settings?: {skipSteps: DataPrepareStep[]}) => {
      const skipSteps = settings?.skipSteps || [];
      let nextStep = getNextStepForGoForward(step);

      while (nextStep && skipSteps.includes(nextStep)) {
        nextStep = getNextStepForGoForward(nextStep);
      }

      if (nextStep) {
        dispatch(setStep(nextStep));
      }
    },
    [dispatch, getNextStepForGoForward, step]
  );

  const goBack = () => {
    if (step === DataPrepareStep.DATA_UPLOAD) {
      dispatch(setStep(DataPrepareStep.SELECT_PREPARE_TYPE));
    } else if (step === DataPrepareStep.DATA_REVIEW) {
      if (imagesExistAlready) {
        dispatch(setStep(DataPrepareStep.SELECT_PREPARE_TYPE));
      } else {
        dispatch(setStep(DataPrepareStep.DATA_UPLOAD));
      }
    } else if (
      [DataPrepareStep.LABEL_UPLOAD, DataPrepareStep.LABEL_REVIEW].includes(step)
    ) {
      dispatch(setStep(DataPrepareStep.CREATING_DATASET));
    } else if (step === DataPrepareStep.SELECT_LABELLING_TYPE) {
      dispatch(setStep(DataPrepareStep.LABEL_REVIEW));
    } else if (
      step === DataPrepareStep.CLASSIFICATION_PREPARE ||
      step === DataPrepareStep.DETECTION_PREPARE
    ) {
      dispatch(setStep(DataPrepareStep.SELECT_LABELLING_TYPE));
    } else if (step === DataPrepareStep.SEGMENTATION_PREPARE) {
      dispatch(setStep(DataPrepareStep.LABEL_REVIEW));
    }
  };

  const renderStep = () => {
    if (step === DataPrepareStep.DEPLOYMENT_DATA_REVIEW) {
      return (
        <DeploymentDataView
          projectId={projectId}
          inProgress={inProgress}
          deploymentId={sourceDeploymentId}
        />
      );
    } else if (step === DataPrepareStep.SELECT_PREPARE_TYPE) {
      return (
        <MethodView
          projectId={projectId}
          inProgress={inProgress}
          showRevert={previousOrigin === 'deployment'}
          dataPreparationContainerId={dataPreparationContainerId}
          onForward={goForward}
          onRevert={handleMergeRevert}
        />
      );
    } else if (
      step === DataPrepareStep.DATA_UPLOAD ||
      step === DataPrepareStep.DATA_REVIEW
    ) {
      return (
        <DataView
          inProgress={inProgress}
          dataPreparationContainer={dataPreparationContainer}
          onBack={goBack}
          onForward={goForward}
        />
      );
    }
    if (step === DataPrepareStep.CREATING_DATASET) {
      return (
        <CreateDatasetStep
          projectId={projectId}
          dataPreparationContainerId={dataPreparationContainerId}
          onForward={goForward}
        />
      );
    } else if (
      step === DataPrepareStep.LABEL_UPLOAD ||
      step === DataPrepareStep.LABEL_REVIEW
    ) {
      return (
        <LabelView
          projectId={projectId}
          inProgress={inProgress}
          dataPreparationContainerId={dataPreparationContainerId}
          onForward={goForward}
          onBack={goBack}
        />
      );
    } else if (step === DataPrepareStep.DATA_REVIEW_FINALIZATION) {
      return (
        <DataUploadingFinalization
          projectId={projectId}
          origin={dataPreparationContainer?.origin || 'raw'}
          dataPreparationContainerId={dataPreparationContainerId}
          onForward={goForward}
        />
      );
    } else if (step === DataPrepareStep.SELECT_LABELLING_TYPE) {
      return (
        <LabelMethodView onForward={goForward} onBack={goBack} projectId={projectId} />
      );
    } else if (step === DataPrepareStep.DETECTION_PREPARE) {
      return (
        <DetectionView
          projectId={projectId}
          inProgress={inProgress}
          origin={dataPreparationContainer?.origin || 'raw'}
          dataPreparationContainerId={dataPreparationContainerId}
          onBack={goBack}
          onSubmit={handleSubmit}
          onUpdateAnnotations={handleSaveAnnotations}
        />
      );
    } else if (step === DataPrepareStep.CLASSIFICATION_PREPARE) {
      return (
        <ClassificationView
          projectId={projectId}
          inProgress={inProgress}
          origin={dataPreparationContainer?.origin || 'raw'}
          dataPreparationContainerId={dataPreparationContainerId}
          onBack={goBack}
          onSubmit={handleSubmit}
        />
      );
    } else if (step === DataPrepareStep.SEGMENTATION_PREPARE) {
      return (
        <SegmentationView
          projectId={projectId}
          inProgress={inProgress}
          origin={dataPreparationContainer?.origin || 'raw'}
          dataPreparationContainerId={dataPreparationContainerId}
          onBack={goBack}
          onSubmit={handleSubmit}
          onUpdateAnnotations={handleSaveAnnotations}
          onSaveAnnotations={saveCurrentAnnotationsState}
        />
      );
    }
  };

  return (
    <main className="data-prepare">
      <Fade in={inProgress}>
        <div className="data-prepare__loading">
          <Waiting
            diameter={100}
            loadingText={intl.formatMessage({
              id: isSubmitting
                ? 'dataPrep.submitting'
                : isUploading
                ? 'dataPrepWithVideoSupport.uploading.image'
                : 'dataPrep.loading',
            })}
          >
            {isUploading && uploadProgress && (
              <FormattedMessage
                tagName="span"
                id="dataPrep.uploading.progress"
                values={uploadProgress}
              />
            )}
          </Waiting>
        </div>
      </Fade>
      {renderStep()}
    </main>
  );
};

export const DataPrepare = (props: DataPrepareBaseProps) => (
  <DataSetJobsProvider>
    <DataPrepareWithVideoSupportBase {...props} />
  </DataSetJobsProvider>
);
